/*
 * Roles, in words a person recognises.
 *
 * `store/permissions.ts` holds what a role may do and is the only authority on
 * that. This file holds how a role is written and said, which is a different
 * question and is asked in two places: on the invitation screen, where somebody
 * is being told what they are about to become before they are asked for a
 * password, and on the refusal screen, where somebody is being told why a door
 * did not open.
 *
 * The summaries are one sentence each and they are written from the reader's
 * side. "field_agent" tells an incoming health worker nothing; "you own the
 * tasks and cases assigned to you, and you can enrol people and file reports"
 * tells them what tomorrow looks like. The wording follows the platform model's
 * own descriptions rather than restating the permission table, because the
 * table changes shape as the product grows and a sentence about browsing the
 * community does not.
 */

import { ORG_ROLES } from '@/api'
import type { OrgRole } from '@/api'
import { can } from '@/store/permissions'
import type { Permission } from '@/store/permissions'

const LABELS: Record<OrgRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  analyst: 'Analyst',
  field_agent: 'Field agent',
  viewer: 'Viewer',
}

const SUMMARIES: Record<OrgRole, string> = {
  owner: 'Everything in this organization, including billing and closing it down.',
  admin: 'Manage staff, projects and settings.',
  manager: 'Run projects: knowledge, broadcasts, cases, tasks and indicators.',
  analyst: 'Read everything and export it. Nothing operational is yours to change.',
  field_agent:
    'Own the tasks and cases assigned to you, enrol people, and file reports. You do not browse the community.',
  viewer: 'Read dashboards and aggregate insight. No individual records.',
}

export function roleLabel(role: OrgRole): string {
  return LABELS[role]
}

export function roleSummary(role: OrgRole): string {
  return SUMMARIES[role]
}

/**
 * Which roles hold a permission, in the order the platform model ranks them.
 *
 * Derived from `can` rather than from a second list, so a refusal screen that
 * says "an owner, an admin or a manager can do this" cannot go stale while the
 * permission table underneath it changes. A hand-written list here would be
 * wrong on exactly the day somebody widens a permission and does not think to
 * look at the copy on a screen they have never seen.
 */
export function rolesWith(permission: Permission): OrgRole[] {
  return ORG_ROLES.filter((role) => can(role, permission))
}
